import { Ref, forwardRef } from 'react'

import { Box, useClassNames, useUnsafeMemo } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { InjectHoverProvider } from 'modules/core/lib/hoverContext'

import type { Column } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import type View from 'modules/shift_management/store/internal/models/View'

import { stylesRefs } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/index.styles'
import HeaderContent from './HeaderContent'
import './index.styles.css'

interface CellProps {
  heading: Column
  index: number
  view: View
  canManageShifts: boolean
  onSelect: (index: number, moment: moment.Moment) => void
  checked: (index: number, dayId: string) => boolean
}

const HeaderCell = (
  { heading, index, onSelect, checked, view, canManageShifts }: CellProps,
  ref: Ref<HTMLDivElement>
) => {
  const { viewType, groupType, lastDay } = view
  const { date } = heading
  const cx = useClassNames(stylesRefs)
  const currentDate = moment().startOf('hour') // Use 'hour' to work with daily view too
  const isToday = dateToString(date, view.dateFormat) === dateToString(currentDate, view.dateFormat)
  // To not highlight first column (employee or work area) that always has the date of today
  const highlighted = isToday && index !== 0
  const dayId = dateToString(date, view.dateFormat)
  const isChecked = checked(index, dayId)
  const sticky = index === 0
  const paddingRight = useUnsafeMemo(() => {
    if (index === 0) return 's4'
    return view.isDaily ? 's6' : view.isWeekly ? 's8' : 's4'
  }, [index, view.isDaily, view.isWeekly])

  const paddingLeft = useUnsafeMemo(() => {
    if (index === 0) return 's1'
    return view.isDaily ? 's6' : 's8'
  }, [index, view.isDaily])

  const onClick = () => {
    track(trackingEvents.shiftManagement.bulkDaySelected, {
      selected: !isChecked,
      view: viewType,
      view_type_by: groupType,
    })
    return onSelect(index, date)
  }
  return (
    <InjectHoverProvider key={index}>
      <Box
        ref={ref}
        background="white"
        height="s32"
        paddingLeft={paddingLeft}
        paddingRight={paddingRight}
        paddingTop="s4"
        paddingBottom="s8"
        border={{
          right: {
            width: 's1',
            style: 'solid',
            color: 'grey200',
          },
          bottom: { width: 's1', style: 'solid', color: 'grey200' },
        }}
        className={cx('header', { sticky })}
      >
        <Box flexDirection="row" alignItems="center" gap="s6" height="full">
          <HeaderContent
            highlighted={highlighted}
            heading={heading}
            index={index}
            onClick={onClick}
            isChecked={isChecked}
            canManageShifts={canManageShifts}
          />
        </Box>
      </Box>
    </InjectHoverProvider>
  )
}

export default observer(forwardRef(HeaderCell))
